import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { utilizadoresAPI } from '../utils/api';
import { usePageTitle } from '../hooks/usePageTitle';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  UserCog,
  CheckCircle,
  XCircle
} from 'lucide-react';
import Swal from 'sweetalert2';

export const Utilizadores = () => {
  const [utilizadores, setUtilizadores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  usePageTitle('Utilizadores');  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    fetchUtilizadores();
  }, []);

  const fetchUtilizadores = async () => {
    try {
      const response = await utilizadoresAPI.getAll();
      setUtilizadores(response.data);
    } catch (error) {
      console.error('Error fetching utilizadores:', error);
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: 'Não foi possível carregar os utilizadores.',
        confirmButtonColor: '#0D9488'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (utilizador) => {
    if (utilizador.id === user?.id) {
      Swal.fire({
        icon: 'warning',
        title: 'Operação não permitida',
        text: 'Não pode eliminar o seu próprio utilizador.',
        confirmButtonColor: '#0D9488'
      });
      return;
    }

    const result = await Swal.fire({
      title: 'Eliminar Utilizador?',
      text: `Tem a certeza que deseja eliminar "${utilizador.nome}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#EF4444',
      cancelButtonColor: '#64748B',
      confirmButtonText: 'Sim, eliminar',
      cancelButtonText: 'Cancelar'
    });

    if (result.isConfirmed) {
      try {
        await utilizadoresAPI.delete(utilizador.id);
        setUtilizadores(utilizadores.filter(u => u.id !== utilizador.id));
        Swal.fire({
          icon: 'success',
          title: 'Eliminado!',
          text: 'Utilizador removido com sucesso.',
          timer: 1500,
          showConfirmButton: false
        });
      } catch (error) {
        console.error('Error deleting utilizador:', error);
        Swal.fire({
          icon: 'error',
          title: 'Erro',
          text: error.response?.data?.detail || 'Não foi possível eliminar o utilizador.',
          confirmButtonColor: '#0D9488'
        });
      }
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-PT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const filteredUtilizadores = utilizadores.filter(utilizador =>
    utilizador.nome?.toLowerCase().includes(search.toLowerCase()) ||
    utilizador.email?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="utilizadores-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Utilizadores</h1>
          <p className="text-slate-500 mt-1">Gestão de acessos à plataforma</p>
        </div>
        <Link to="/utilizadores/novo">
          <Button className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="add-utilizador-btn">
            <Plus className="h-4 w-4 mr-2" />
            Novo Utilizador
          </Button>
        </Link>
      </div>

      {/* Search */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Pesquisar por nome ou email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="search-utilizadores-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-slate-200">
        <CardHeader className="pb-4">
          <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <UserCog className="h-5 w-5 text-amber-600" />
            Lista de Utilizadores ({filteredUtilizadores.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-slate-500">A carregar...</div>
          ) : filteredUtilizadores.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              <UserCog className="h-12 w-12 mx-auto text-slate-300 mb-3" />
              <p>{search ? 'Nenhum utilizador encontrado' : 'Nenhum utilizador registado'}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-semibold text-slate-700">Nome</TableHead>
                    <TableHead className="font-semibold text-slate-700">Email</TableHead>
                    <TableHead className="font-semibold text-slate-700">Estado</TableHead>
                    <TableHead className="font-semibold text-slate-700">Criado em</TableHead>
                    <TableHead className="font-semibold text-slate-700 text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUtilizadores.map((utilizador) => (
                    <TableRow key={utilizador.id} className="hover:bg-slate-50" data-testid={`utilizador-row-${utilizador.id}`}>
                      <TableCell className="font-medium text-slate-900">
                        <div className="flex items-center gap-3">
                          <div className="h-9 w-9 rounded-full bg-purple-100 flex items-center justify-center">
                            <span className="text-sm font-semibold text-purple-700">
                              {utilizador.nome?.charAt(0)?.toUpperCase() || 'U'}
                            </span>
                          </div>
                          <div>
                            {utilizador.nome}
                            {utilizador.id === user?.id && (
                              <span className="ml-2 text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">
                                Você
                              </span>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-600">
                        {utilizador.email}
                      </TableCell>
                      <TableCell>
                        {utilizador.ativo ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-50 text-green-700 rounded-full text-sm">
                            <CheckCircle className="h-3.5 w-3.5" />
                            Ativo
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-50 text-red-700 rounded-full text-sm">
                            <XCircle className="h-3.5 w-3.5" />
                            Inativo
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-slate-600">
                        {formatDate(utilizador.created_at)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => navigate(`/utilizadores/${utilizador.id}/editar`)}
                            data-testid={`edit-utilizador-${utilizador.id}`}
                          >
                            <Edit className="h-4 w-4 text-slate-500" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(utilizador)}
                            disabled={utilizador.id === user?.id}
                            data-testid={`delete-utilizador-${utilizador.id}`}
                          >
                            <Trash2 className={`h-4 w-4 ${utilizador.id === user?.id ? 'text-slate-300' : 'text-red-500'}`} />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
